const cleanups = new Map<HTMLElement, () => void>();

export function teardownTaskDelivery() {
  for (const cleanup of cleanups.values()) cleanup();
  cleanups.clear();
}

export function initTaskDelivery(reload = () => window.location.reload()) {
  document.querySelectorAll<HTMLElement>("[data-task-refresh-url]").forEach((element) => {
    if (cleanups.has(element)) return;
    const endpoint = new URL(element.dataset.taskRefreshUrl!, window.location.href);
    if (endpoint.origin !== window.location.origin) return;
    const version = element.dataset.taskRefreshVersion;
    const form = element.querySelector<HTMLFormElement>("[data-task-send-form]");
    let sending = element.dataset.taskSending === "true";
    let submitting = false;
    let stopped = false;
    let failures = 0;
    let attempts = 0;
    let dirty = element.dataset.taskDirty === "true";
    let timer: ReturnType<typeof setTimeout> | undefined;
    let request: AbortController | undefined;

    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      request?.abort();
    };
    const cleanup = () => {
      stop();
      window.removeEventListener("focus", onReturn);
      window.removeEventListener("pageshow", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
      form?.removeEventListener("submit", onSubmit);
      element.removeEventListener("input", onInput);
      element.removeEventListener("change", onInput);
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!stopped && sending && !submitting && failures < 3 && attempts < 100) timer = setTimeout(check, 3000);
    };
    const check = async () => {
      if (stopped || submitting || request || document.visibilityState === "hidden") return;
      if (!element.isConnected) return stop();
      request = new AbortController();
      attempts += 1;
      const timeout = setTimeout(() => request?.abort(), 10000);
      try {
        const response = await fetch(endpoint.href, {
          credentials: "same-origin", cache: "no-store", redirect: "error",
          headers: { Accept: "application/json" }, signal: request.signal,
        });
        if ([401, 403, 404].includes(response.status)) return stop();
        if (!response.ok) throw new Error("Task status unavailable");
        const result = await response.json();
        if (stopped || submitting || !element.isConnected) return;
        if (typeof result.version !== "string" || typeof result.sending !== "boolean") throw new Error("Invalid task status");
        if (result.version !== version) {
          stop();
          if (dirty) {
            const notice = document.createElement("p");
            notice.setAttribute("role", "status");
            notice.textContent = "Task status changed. Your unsaved edits are still here. ";
            const link = document.createElement("a");
            link.href = window.location.href;
            link.textContent = "Refresh task status";
            link.className = "underline";
            notice.append(link);
            element.prepend(notice);
          } else {
            reload();
          }
          return;
        }
        sending = result.sending;
        if (!sending && form?.dataset.taskSubmitting === "true") {
          form.removeAttribute("aria-busy");
          form.querySelectorAll<HTMLInputElement>("input[data-task-original-label]").forEach((button) => {
            button.disabled = false;
            button.value = button.dataset.taskOriginalLabel!;
          });
          delete form.dataset.taskSubmitting;
          element.dataset.taskSending = "false";
          const progress = element.querySelector("[data-task-send-progress]");
          if (progress) progress.textContent = "";
        }
        failures = 0;
      } catch {
        if (stopped) return;
        failures += 1;
        if (failures >= 3) {
          const progress = element.querySelector("[data-task-send-progress]");
          if (progress) progress.textContent = "Automatic status checks could not connect. Refresh to check progress before sending again.";
        }
      } finally {
        clearTimeout(timeout);
        request = undefined;
      }
      schedule();
      if (attempts >= 100 && sending) {
        const progress = element.querySelector("[data-task-send-progress]");
        if (progress) progress.textContent = "Sending is taking longer than expected. Refresh to check progress; do not send again.";
      }
    };
    const onReturn = () => {
      if (document.visibilityState === "hidden") return;
      failures = 0;
      attempts = 0;
      clearTimeout(timer);
      void check();
    };
    const onSubmit = (event: SubmitEvent) => {
      if (event.defaultPrevented) return;
      if (submitting || sending) {
        event.preventDefault();
        return;
      }
      // Let the normal form navigation and server redirect finish. Polling
      // during that request could replace the page before the send completes.
      submitting = true;
      dirty = false;
      delete element.dataset.taskDirty;
      element.dataset.taskSending = "true";
      clearTimeout(timer);
      form?.setAttribute("aria-busy", "true");
      if (form) form.dataset.taskSubmitting = "true";
      form?.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input[type='submit'], button[type='submit']").forEach((button) => {
        button.disabled = true;
        if (button instanceof HTMLInputElement) {
          button.dataset.taskOriginalLabel ||= button.value;
          button.value = "Sending…";
        } else button.textContent = "Sending…";
      });
      const progress = element.querySelector("[data-task-send-progress]");
      if (progress) progress.textContent = "Sending review email… This may take a moment. Please wait for confirmation.";
    };
    const onInput = () => {
      dirty = true;
      element.dataset.taskDirty = "true";
    };

    cleanups.set(element, cleanup);
    form?.addEventListener("submit", onSubmit);
    element.addEventListener("input", onInput);
    element.addEventListener("change", onInput);
    window.addEventListener("focus", onReturn);
    window.addEventListener("pageshow", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    void check();
  });
}
